// Печатният документ „Изписване/Бракуване на стока": вид, номер и статус в
// заглавието; банер „Анулиран на … · причина"; таблицата; футър Анулирай ·
// Редактирай | PDF · Принтирай. Действията по документа се показват само когато
// е отворен от ред и е активен (не след запис). `autoPrint` печата веднага след
// отваряне („Печат" от реда в справката).
//
// `@page { size: A4; margin: 0 }` и двойката writeoff-doc-paper /
// printing-writeoff са НАРОЧНО различни от другите документни модали.
import { useEffect } from "react";
import { Modal } from "antd";
import { CloseOutlined, EditOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { modalStyles } from "../../../../utils/modalStyles";
import { useViewport } from "../../../../hooks/useViewport";
import { useProducts } from "../../../../hooks/useInventory";
import { formatQty, useUnitDecimals } from "../../components/qty";
import { formatMoney, formatUnitPrice } from "../../components/numbers";
import { fmtDate } from "../../components/dateTime";
import { StatusPill } from "../../components/list/ListBits";
import {
  DANGER_TEXT_BUTTON_CLASS,
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
} from "../../components/list/buttonStyles";
import type { Writeoff } from "../../../../api/inventory";
import { DocumentRevisionsPanel } from "../../components/revisions/DocumentRevisionsPanel";
import { WriteoffKindBadge } from "./WriteoffKindBadge";
import { isWriteoffAnnulled } from "./writeoffMeta";

// Порции (множител ×N на рецепта) — без излишни нули (×3, а не ×3,000).
const formatPortions = (value: number) =>
  Number(value ?? 0).toLocaleString("bg-BG", { maximumFractionDigits: 3 });

type Props = {
  open: boolean;
  onClose: () => void;
  writeoff: Writeoff | null;
  onEdit?: () => void;
  onAnnul?: () => void;
  autoPrint?: boolean;
};

// Печат само на документа: изваждаме .writeoff-doc-paper в <body>, маркираме
// body с .printing-writeoff (CSS скрива всичко останало), печатаме и връщаме
// node-а на мястото му. Същият подход като при доставката.
const printDocument = () => {
  const paper = document.querySelector(".writeoff-doc-paper") as HTMLElement | null;
  if (!paper || !paper.parentElement) {
    window.print();
    return;
  }
  const parent = paper.parentElement;
  const placeholder = document.createComment("writeoff-doc-placeholder");
  parent.insertBefore(placeholder, paper);
  document.body.appendChild(paper);
  document.body.classList.add("printing-writeoff");
  const restore = () => {
    document.body.classList.remove("printing-writeoff");
    if (placeholder.parentElement) parent.insertBefore(paper, placeholder);
    placeholder.remove();
    window.removeEventListener("afterprint", restore);
  };
  window.addEventListener("afterprint", restore);
  requestAnimationFrame(() => window.print());
};

export const WriteoffDocumentModal = ({
  open,
  onClose,
  writeoff,
  onEdit,
  onAnnul,
  autoPrint = false,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useViewport();
  const { data: products = [] } = useProducts();
  const decimalsOf = useUnitDecimals();
  // Мерна единица по продукт (показва се както е записана — напр. „килограм", „брой").
  const unitByProduct = new Map(products.map((p) => [p.id, p.unit]));

  useEffect(() => {
    if (!open || !autoPrint || !writeoff) return;
    const timer = setTimeout(printDocument, 250);
    return () => clearTimeout(timer);
  }, [open, autoPrint, writeoff]);

  if (!writeoff) return null;
  // Видът идва от самия запис (обща страница изписване/бракуване).
  const isScrap = writeoff.kind === "scrap";
  const annulled = isWriteoffAnnulled(writeoff);
  const width = isMobile ? undefined : isTablet ? 720 : 860;
  const subtitle = [
    fmtDate(writeoff.date),
    writeoff.warehouseName || null,
    writeoff.createdByName ? t("inventoryPage.enteredBy", { name: writeoff.createdByName }) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const info: { label: string; value: string }[] = [
    { label: t("inventoryPage.colDate"), value: fmtDate(writeoff.date) },
    { label: t("inventoryPage.fieldReason"), value: writeoff.reason || "—" },
    { label: t("inventoryPage.colWarehouse"), value: writeoff.warehouseName || "—" },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 20px)" }}
    >
      {/* Печатна механика + таблицата на самия документ — scoped CSS */}
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body.printing-writeoff > *:not(.writeoff-doc-paper) { display: none !important; }
          body.printing-writeoff { background: #fff !important; }
          body.printing-writeoff .writeoff-doc-paper {
            display: block !important;
            position: static !important;
            box-shadow: none !important;
            padding: 14mm 12mm !important;
          }
        }
        .writeoff-doc table { width: 100%; border-collapse: collapse; }
        .writeoff-doc th, .writeoff-doc td {
          border: 1px solid #C9C5BC; padding: 6px 8px; font-size: 12px;
        }
        .writeoff-doc th {
          background: #F0EDE6; text-align: left; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.4px; font-size: 10px;
          color: #52525B;
        }
        .writeoff-doc td.num, .writeoff-doc th.num { text-align: right; font-variant-numeric: tabular-nums; }
        .writeoff-doc .annulled-stamp { color: #b42318; font-weight: 700; letter-spacing: 0.1em; }
      `}</style>

      <div className="flex max-h-[90vh] flex-col bg-surface">
        {/* Заглавие (не се печата) */}
        <div
          className={`flex items-start gap-3 border-b border-shade-2 ${
            isMobile ? "px-4 py-3.5" : "px-[22px] py-[18px]"
          }`}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <WriteoffKindBadge kind={writeoff.kind} />
              <span className="text-lg font-semibold leading-[1.2] text-ink">
                {t(
                  isScrap ? "inventoryPage.docScrapSubtitle" : "inventoryPage.docWriteoffSubtitle",
                  { id: writeoff.id },
                )}
              </span>
              {annulled && <StatusPill tone="neutral">{t("inventoryPage.statusAnnulled")}</StatusPill>}
            </div>
            <span className="text-[13px] leading-[normal] text-body">{subtitle}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("inventoryPage.close")}
            className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted hover:text-ink"
          >
            <CloseOutlined className="text-sm" />
          </button>
        </div>

        {annulled && (
          <div
            className={`rounded-[9px] bg-fill-2 px-3.5 py-2.5 text-[13.5px] leading-[1.45] text-ink-2 ${
              isMobile ? "mx-4 mt-3.5" : "mx-[22px] mt-3.5"
            }`}
          >
            {t("inventoryPage.annulledBanner", {
              date: writeoff.deletedAt ? dayjs(writeoff.deletedAt).format("DD.MM.YYYY HH:mm") : "—",
            })}
            {writeoff.deletedByName ? ` · ${writeoff.deletedByName}` : ""}
            {writeoff.deleteReason ? ` · ${writeoff.deleteReason}` : ""}
          </div>
        )}

        {/* Документ (печата се само това) */}
        <div className={`min-h-0 overflow-y-auto ${isMobile ? "p-4" : "px-[22px] py-4"}`}>
          {/* История на промените — над листа, не се печата */}
          <DocumentRevisionsPanel key={writeoff.id} group="writeoffs" movementId={writeoff.id} />
          <div className="writeoff-doc writeoff-doc-paper bg-surface text-ink">
            <h2 className="mb-3 mt-0 flex items-center gap-3 text-lg font-extrabold leading-[normal] text-ink">
              {t(isScrap ? "inventoryPage.docScrapHeading" : "inventoryPage.docWriteoffHeading")}
              {annulled && (
                <span className="annulled-stamp text-sm">{t("inventoryPage.statusAnnulledStamp")}</span>
              )}
            </h2>
            <div className={`mb-4 grid gap-2.5 ${isMobile ? "grid-cols-1" : "grid-cols-3"}`}>
              {info.map((item) => (
                <div key={item.label}>
                  <div className="text-[9.5px] font-bold uppercase leading-[normal] tracking-[0.6px] text-muted">
                    {item.label}
                  </div>
                  <div className="mt-0.5 text-[13px] leading-[normal] text-ink">{item.value}</div>
                </div>
              ))}
            </div>

            <table>
              <thead>
                <tr>
                  <th>{t("inventoryPage.colProductName")}</th>
                  <th>{t("inventoryPage.recipe")}</th>
                  <th>{t("inventoryPage.colBatch")}</th>
                  <th className="num">{t("inventoryPage.colQty")}</th>
                  <th>{t("inventoryPage.colUnit")}</th>
                  <th className="num">{t("inventoryPage.colUnitPrice")}</th>
                  <th className="num">{t("inventoryPage.colNet")}</th>
                  <th className="num">{t("inventoryPage.colGross")}</th>
                </tr>
              </thead>
              <tbody>
                {writeoff.lines.map((line) => (
                  <tr key={line.id}>
                    <td>{line.productName}</td>
                    <td>
                      {line.recipeName
                        ? `${line.recipeName}${line.recipeQuantity ? ` ×${formatPortions(line.recipeQuantity)}` : ""}`
                        : "—"}
                    </td>
                    <td>{line.batchNumber || t("inventoryPage.fifo")}</td>
                    <td className="num">{formatQty(line.quantity, decimalsOf(line.productId))}</td>
                    <td>{unitByProduct.get(line.productId) || "—"}</td>
                    {/* ед. цена с до 5 знака (Numeric(13,5)) — иначе к-во × цена
                        спира да дава показаната сума */}
                    <td className="num">{formatUnitPrice(line.unitPrice)}</td>
                    <td className="num">{formatMoney(line.sumNoVat)}</td>
                    <td className="num">{formatMoney(line.sumWithVat)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={6} className="font-bold">
                    {t("inventoryPage.total")}
                  </td>
                  <td className="num font-extrabold">{formatMoney(writeoff.totalValueNoVat)}</td>
                  <td className="num font-extrabold">{formatMoney(writeoff.totalValueWithVat)}</td>
                </tr>
              </tbody>
            </table>

            {writeoff.comment && (
              <div className="mt-3.5 text-xs leading-[normal] text-body">
                <span className="font-bold">{t("inventoryPage.fieldComment")}: </span>
                {writeoff.comment}
              </div>
            )}
          </div>
        </div>

        {/* Футър (не се печата) */}
        <div
          className={`print:hidden flex flex-wrap items-center gap-2 border-t border-shade-2 ${
            isMobile ? "px-4 py-3" : "px-[22px] pb-[18px] pt-3.5"
          }`}
        >
          {!annulled && onAnnul && (
            <button type="button" onClick={onAnnul} className={DANGER_TEXT_BUTTON_CLASS}>
              {t("inventoryPage.docDelete")}
            </button>
          )}
          {!annulled && onEdit && (
            <button type="button" onClick={onEdit} className={SECONDARY_BUTTON_CLASS}>
              <EditOutlined />
              {t("inventoryPage.docEdit")}
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={printDocument}
            title={t("inventoryPage.pdfHint")}
            className={SECONDARY_BUTTON_CLASS}
          >
            PDF
          </button>
          <button type="button" onClick={printDocument} className={PRIMARY_BUTTON_CLASS}>
            <PrinterOutlined className="text-[15px]" />
            {t("inventoryPage.print")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default WriteoffDocumentModal;
